// Timeline sweep helper — used when the asset-length control changes and
// existing chips may now exceed the new totalDuration.
//
// Strategy: chips stay exactly where they are as long as they fit into the
// display's 2× overtime slack (the timeline simply enters overtime — GitHub
// #1, point 3). Only chips that would end beyond `totalDuration * 2` (which
// the Timeline can no longer render) are pulled back to that cap, then piped
// through the no-pin sweep branch of the resolver (pinIdx = -1) so pulled
// chips that now collide with their neighbours get pushed right.
//
// Called from AssetLengthControl.onChange / FreeAssetHeader. Legacy
// (index.html ~6170) hard-clamped to totalDuration instead — that squashed
// every chip into the new length, which is exactly the "chips shove into
// each other" bug reported in #1.

import type { Block } from '@shared/types';
import { resolveTimelineOverlapsPerTrack } from './timeline';

function snap01(x: number): number {
  return Math.round(x * 10) / 10;
}

/**
 * Clamp each favourited chip's offset so chip end ≤ totalDuration * 2 (the
 * overtime cap), then run the no-pin resolver sweep. Returns a new `Block[]`
 * with updated offsets; returns the input array unchanged if no clamp was
 * needed — the common case when the length merely shrinks below the content.
 *
 * `assetVoiceIds` is used by the per-track sweep so cross-voice chips can keep
 * overlapping each other (only same-track collisions get resolved). Pass `[]`
 * if multi-voice context is unavailable — all chips group together as a single
 * pseudo-track which mirrors the pre-multi-voice behavior.
 */
export function sweepClampOffsets(
  blocks: Block[],
  totalDuration: number,
  assetVoiceIds: string[] = [],
): Block[] {
  if (!blocks.length || !(totalDuration > 0)) return blocks;
  // Same cap as the resolver's maxAllowed and the Timeline's OVERTIME_MULT.
  const cap = totalDuration * 2;
  let changed = false;
  const clamped = blocks.map((b) => {
    if (b.favIdx < 0) return b;
    const take = b.takes[b.favIdx];
    if (!take) return b;
    const dur = take.duration || 0;
    const maxOff = Math.max(0, cap - dur);
    const currOff = b.offset || 0;
    if (currOff > maxOff) {
      changed = true;
      return { ...b, offset: snap01(maxOff) };
    }
    return b;
  });
  if (!changed) return blocks;
  // No-pin sweep, per-voice-track: if the clamp made two same-track chips
  // overlap, the resolver shifts the right-hand chip onto the first free
  // slot after its predecessor. Cross-track overlaps remain allowed.
  return resolveTimelineOverlapsPerTrack(clamped, assetVoiceIds, -1, totalDuration);
}
